import { decode } from 'html-entities';
import { FormattedVideoItem } from '@benzinga/videos-manager';
import { VIDEO_ARTICLE_STORY_TYPE } from '@benzinga/article-manager';
import type { StoryObject } from '@benzinga/advanced-news-manager';
import { InternalNewsManager } from '@benzinga/internal-news-manager';
import { MetaProps, PageType } from '@benzinga/seo';
import { getGlobalSession } from '../../../src/utils/session';
import { ContentManager, WordpressSidebar } from '@benzinga/content-manager';
import { loadServerSideBlockData } from '@benzinga/blocks-utils/load';
import { type CacheableResult } from '../../../utils/redis/redis-wrapper';

export const WATCH_CANONICAL = 'https://www.benzinga.com/watch';
export const WATCH_ARTICLE_PATH_PREFIX = 'watch/';
const WATCH_ARTICLE_FETCH_LIMIT = 50;

export const extractWatchArticleNodeIdFromSlug = (slug: string): string | null => {
  const normalizedSlug = slug.trim();
  const nodeIdMatch = normalizedSlug.match(/-(\d+)$/);
  return nodeIdMatch?.[1] ?? null;
};

export const isWatchArticleCanonicalSlug = (slug: string, canonicalPath: string | null | undefined): boolean => {
  if (!canonicalPath) return false;
  const normalizedCanonicalPath = canonicalPath.replace(/^\/+/, '').replace(/\/+$/, '');
  return normalizedCanonicalPath === `${WATCH_ARTICLE_PATH_PREFIX}${slug}`;
};

export const WATCH_TITLE = 'Watch Live Stock Market News';
export const WATCH_DESCRIPTION =
  'Watch Benzinga TV: live earnings calls, market commentary, podcasts, webinars, and trading sessions.';

export const VIDEO_FILTER_CATEGORIES = [
  'All',
  'General',
  'Podcast',
  'Webinars',
  'Live Trading',
  'PreMarket Playbook',
] as const;

export type VideoCategoryKey = (typeof VIDEO_FILTER_CATEGORIES)[number];

const GENERIC_TAB_NAMES = new Set(['all', 'news', 'top stories', 'benzinga', 'video', 'videos', 'watch']);
const FILTER_CATEGORY_NAMES = VIDEO_FILTER_CATEGORIES.filter(category => category !== 'All');
const DEFAULT_VIDEO_CATEGORY: VideoCategoryKey = 'General';

interface WatchArticleItem extends FormattedVideoItem {
  categories: string[];
  publishedAt?: string;
}

export interface VideoCategoryGroup {
  category: VideoCategoryKey;
  items: FormattedVideoItem[];
}

export interface WatchPageProps {
  pageTargeting: Record<string, string | string[]>;
  sidebar: WordpressSidebar | null;
  videoCategories: VideoCategoryGroup[];
}

const youtubeThumbnailUrl = (youtubeId: string): string => `https://i.ytimg.com/vi/${youtubeId}/hq720.jpg`;

const normalizeCategory = (value: string | undefined): string => value?.trim().toLowerCase() ?? '';

const collectItemCategories = (story: StoryObject): string[] => {
  const names = new Map<string, string>();
  const addCategory = (raw: string | undefined) => {
    const trimmed = raw?.trim();
    if (!trimmed || GENERIC_TAB_NAMES.has(normalizeCategory(trimmed))) return;
    const knownCategory = FILTER_CATEGORY_NAMES.find(
      category => normalizeCategory(category) === normalizeCategory(trimmed),
    );
    if (!knownCategory) return;
    names.set(normalizeCategory(knownCategory), knownCategory);
  };

  addCategory(story.meta?.wp_post_meta?.VideoCategory);
  for (const channel of story.channels ?? []) {
    addCategory(channel?.name);
  }

  return names.size > 0 ? [...names.values()] : [DEFAULT_VIDEO_CATEGORY];
};

const toWatchArticleItem = (story: StoryObject): WatchArticleItem | null => {
  const href = story.url?.trim();
  const title = story.title?.trim();
  if (!href || !title) return null;

  const videoMeta = story.meta?.wp_post_meta;
  const youtubeId = videoMeta?.VideoID;
  const image = (youtubeId ? youtubeThumbnailUrl(youtubeId) : '') || videoMeta?.VideoThumbnail || story.image || '';

  return {
    categories: collectItemCategories(story),
    href,
    id: String(story.nodeId),
    image,
    publishedAt: typeof story.created === 'string' ? story.created : '',
    title: decode(title),
  };
};

export const buildWatchArticleCategories = (stories: StoryObject[]): VideoCategoryGroup[] => {
  const items = stories.map(toWatchArticleItem).filter((item): item is WatchArticleItem => item !== null);

  return VIDEO_FILTER_CATEGORIES.map(category => ({
    category,
    items:
      category === 'All'
        ? items
        : items.filter(item => item.categories.some(name => normalizeCategory(name) === normalizeCategory(category))),
  }));
};

export async function loadWatchPageProps(): Promise<CacheableResult<WatchPageProps>> {
  const session = getGlobalSession();
  const contentManager = session.getManager(ContentManager);
  const internalNewsManager = session.getManager(InternalNewsManager);
  const sidebarReq = contentManager.getWordpressPost(333491);
  const watchArticlesReq = internalNewsManager.getStories(null, {
    displayOutput: 'abstract',
    limit: WATCH_ARTICLE_FETCH_LIMIT,
    type: VIDEO_ARTICLE_STORY_TYPE,
  });

  const [watchArticlesRes, sidebarRes] = await Promise.all([watchArticlesReq, sidebarReq]);

  if (watchArticlesRes?.err) {
    console.error('Failed to fetch bz_watch articles for /watch:', watchArticlesRes.err);
  }

  const videoCategories = buildWatchArticleCategories(watchArticlesRes?.ok ?? []);

  if (Array.isArray(sidebarRes?.ok?.blocks)) {
    sidebarRes.ok.blocks = await loadServerSideBlockData(session, sidebarRes.ok.blocks);
  }

  return {
    data: {
      pageTargeting: { BZ_CHANNEL: ['Watch Live Stock Market News'], BZ_PTYPE: 'website' },
      sidebar: (sidebarRes?.ok as WordpressSidebar) || null,
      videoCategories,
    },
    originalResponses: { sidebarRes, watchArticlesRes },
  };
}

export interface ParsedWatchVideoTitle {
  cleanedTitle: string;
  ticker?: string;
}

export const buildWatchCollectionItemList = (videos: FormattedVideoItem[]): Record<string, unknown> | undefined => {
  const items: { name: string; url: string }[] = [];

  for (const video of videos) {
    const url = video.href?.trim();
    const name = parseWatchVideoTitle(video.title).cleanedTitle;
    if (!url || !name) continue;
    items.push({ name, url });
  }

  if (items.length === 0) return undefined;

  return {
    '@type': 'ItemList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: item.url,
      name: item.name,
    })),
  };
};

export const buildWatchMetaProps = (videoCategories: VideoCategoryGroup[]): MetaProps => {
  const allVideos = videoCategories.find(group => group.category === 'All')?.items ?? [];
  const collectionItemList = buildWatchCollectionItemList(allVideos);

  return {
    author: 'Benzinga',
    canonical: WATCH_CANONICAL,
    description: WATCH_DESCRIPTION,
    dimensions: {
      authorName: 'Benzinga',
      contentType: 'tool',
    },
    pageType: PageType.Front,
    structuredData: {
      breadcrumb: [
        { name: 'Home', url: '/' },
        { name: 'Watch', url: WATCH_CANONICAL },
      ],
      keywords: VIDEO_FILTER_CATEGORIES.filter(category => category !== 'All'),
      pageType: 'CollectionPage',
      ...(collectionItemList ? { mainEntity: collectionItemList } : {}),
    },
    title: WATCH_TITLE,
  };
};

export const parseWatchVideoTitle = (rawTitle: string): ParsedWatchVideoTitle => {
  const plainTitle = decode(rawTitle.replace(/<[^>]*>/g, ''));
  const tickerMatch = plainTitle.match(/\|\s*\$([A-Z][A-Z0-9.-]*)\s*(?:\||$)/);
  const cleanedTitle = plainTitle
    .replace(/\s*\|\s*🔴?\s*WATCH\s+LIVE\s*/gi, '')
    .replace(/\s*\|\s*\$[A-Z][A-Z0-9.-]*\s*/g, '')
    .replace(/\s*\|\s*$/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();

  return {
    cleanedTitle,
    ticker: tickerMatch?.[1],
  };
};
